import debounce from 'lodash/debounce';
import { useState, useEffect, useCallback, useMemo } from 'react';

/**
 * Custom hook to retry a request based on a condition, with loading state.
 *
 * @param request - The request function to retry.
 * @param args - Arguments to pass to the request function.
 * @param condition - A boolean or a function that returns a boolean to determine if the request should be retried.
 * @param callback - Optional callback to execute after the request is completed.
 */
const useRetryRequest = (
  request: (...args: any[]) => Promise<any>,
  args: any[] = [],
  condition: (() => boolean) | boolean,
  callback?: () => void,
) => {
  const [isLoading, setIsLoading] = useState(false);

  const executeRequest = useMemo(
    () =>
      debounce(() => {
        if (isLoading) return; // Prevent execution if already loading

        setIsLoading(true);
        request(...(args || []))
          .then(() => {
            callback?.();
          })
          .finally(() => {
            setIsLoading(false); // Reset loading state
          });
      }, 500),
    [args, request, callback, isLoading],
  );

  useEffect(() => {
    // Check if retry is requested and condition is met
    const shouldRetry = typeof condition === 'function' ? condition() : condition;

    if (shouldRetry && !isLoading) {
      executeRequest();
    }
  }, [condition, executeRequest, isLoading]);

  // Expose the retry trigger and loading state to be used by the component
  return { isLoading };
};

export default useRetryRequest;
